import { Layout } from 'antd'
import { FunctionComponent, ReactNode, useEffect, useState } from 'react'
import { useToggle } from 'react-use'
import styled from 'styled-components'
import ChatroomWrapper from '@/components/campaign/chatroom-wrapper'
import { AddKolsToCampaignModal } from '@/components/common/modals/add-kols-to-campaign-modal'
import { AddKolsToReportModal } from '@/components/common/modals/add-kols-to-report-modal'
import { AddKolsToPredictiveReportModalProvider } from '@/components/common/modals/hooks/use-add-kols-to-predictive-report-modal'
import NoStyleNativeButton from '@/components/common/no-style-button'
import KolCooperationDetailDrawer from '@/components/kol/common/kol-cooperation-detail-drawer'
import IrmContent from '@/components/layout/irm/irm-content'
import IrmFeatureSiderContent from '@/components/layout/irm/irm-feature-sider-content'
import IrmFooter from '@/components/layout/irm/irm-footer'
import IrmHeader from '@/components/layout/irm/irm-header'
import IrmSettingSiderContent from '@/components/layout/irm/irm-setting-sider-content'
import IrmSider from '@/components/layout/irm/irm-sider'
import { PurchaseEnterpriseModal } from '@/components/workspace/plan/purchase-enterprise-modal'
import LocalStorageKeys from '@/constants/localstorage-keys'
import { useBreakpoint } from '@/hooks/use-breakpoint'
import useLocalStorageSafely from '@/hooks/use-local-storage-safely'
import { useSelector } from '@/hooks/use-selector'
import { reduxAPI } from '@/store/redux-api'

export const SIDER_COLLAPSED_WIDTH = 80
export const SIDER_EXPANDED_WIDTH = 220

type LayoutType = 'feature' | 'setting'

export interface LayoutProps {
  layout?: LayoutType
  children?: ReactNode
  isHeaderSticky?: boolean
}

/**
 * @todo PubNubProvider common wrapper
 */
export const IRMLayout: FunctionComponent<LayoutProps> = ({
  layout = 'feature',
  children,
  isHeaderSticky,
}) => {
  const breakpoint = useBreakpoint()
  const isMobile = breakpoint === 'mobile'

  const { tourModalVisible } = useSelector((state) => state.tour)
  const modal = reduxAPI.state.modal.hooks.useVisible('onboardingModal')
  const [isSiderCollapsedFromStorage, setIsSiderCollapsed] =
    useLocalStorageSafely<string>(LocalStorageKeys.SiderCollapsed)
  const [isSiderDefaultCollapsed, toggleSiderCollapse] = useToggle(
    isSiderCollapsedFromStorage === 'true',
  )
  const [isHoverSider, setIsHoverSider] = useState(false)

  const isSiderCollapsed = ((): boolean => {
    if (tourModalVisible || modal.visible) {
      return false
    }

    return isMobile ? true : isSiderDefaultCollapsed
  })()

  useEffect(() => {
    setIsSiderCollapsed(isSiderDefaultCollapsed.toString())
  }, [isSiderDefaultCollapsed, setIsSiderCollapsed])

  return (
    <StyledLayout $isSiderCollapsed={isSiderCollapsed}>
      <SiderWrapper
        onMouseEnter={(): void => setIsHoverSider(true)}
        onMouseLeave={(): void => setIsHoverSider(false)}
      >
        <IrmSider
          isHoverSider={isHoverSider}
          isSiderCollapsed={isSiderCollapsed}
          toggleSiderCollapse={(): void => {
            toggleSiderCollapse()
            setIsHoverSider(false)
          }}
        >
          {layout === 'feature' && (
            <IrmFeatureSiderContent
              isCollapsed={isSiderCollapsed && !isHoverSider}
            />
          )}
          {layout === 'setting' && (
            <IrmSettingSiderContent
              isCollapsed={isSiderCollapsed && !isHoverSider}
            />
          )}
        </IrmSider>
      </SiderWrapper>
      <Layout>
        <ChatroomWrapper>
          <IrmHeader
            isCollapsed={isSiderCollapsed}
            isSticky={isHeaderSticky}
            layout={layout}
          />
          <IrmContent isCollapsed={isSiderCollapsed}>{children}</IrmContent>
          <IrmFooter isCollapsed={isSiderCollapsed} />
        </ChatroomWrapper>
      </Layout>
      <PurchaseEnterpriseModal />
      <AddKolsToPredictiveReportModalProvider>
        <KolCooperationDetailDrawer />
      </AddKolsToPredictiveReportModalProvider>
      <AddKolsToCampaignModal />
      <AddKolsToReportModal />
    </StyledLayout>
  )
}

const StyledLayout = styled(Layout)<{ $isSiderCollapsed: boolean }>`
  .ant-layout {
    background-color: ${(props): string =>
      props.theme.colors.background.grey} !important;
  }

  .ant-layout-sider {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 110;
    min-width: ${({ $isSiderCollapsed }): string =>
      `${$isSiderCollapsed ? SIDER_COLLAPSED_WIDTH : SIDER_EXPANDED_WIDTH}px`}!important;
  }
`

const SiderWrapper = styled(NoStyleNativeButton)`
  height: 100%;
`
